/** @jsxImportSource @emotion/react */
import React, { useEffect, useState } from 'react';

import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';

import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import axios from 'axios';
import { apiBaseUrl } from '../../../../utils/apiBaseUrl';

import LogoRMF from '../../../../assets/images/fullLogo.png';
import HeroContent from '../../Contents/HeroContent/HeroContent';
import PasswordStrengthIndicator, {
  passwordRegex,
} from '../../components/PasswordStrengthIndicator/PasswordStrengthIndicator';
import { heroStackSx, formPanelSx } from './styles';
import { LAYOUT } from '../../styles/layout';
import { registerColors } from '../../styles/colors';


const ResetPassword = () => {
  const { t } = useTranslation('passwordReset');
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const token = queryParams.get('token');

  const [tokenIsValid, setTokenIsValid] = useState<boolean | null>(null);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [isReset, setIsReset] = useState(false);
  const [passwordFocused, setPasswordFocused] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [confirmPasswordError, setConfirmPasswordError] = useState('');


  useEffect(() => {
    axios
      .get(
        `${apiBaseUrl}password-reset/verify?token=${token}`,
      )
      .then(() => {
        setTokenIsValid(true);
      })
      .catch(() => {
        setTokenIsValid(false);
      });
  }, [token]);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setPasswordError('');
    setErrorMessage('');

    if (!passwordRegex.test(password)) {
      setPasswordError(t('RESET_PASSWORD_DO_NOT_MATCH_CRITERIA'));
      return;
    }

    if (password !== confirmPassword) {
      setConfirmPasswordError(t('RESET_ERROR_PASSWORDS_DO_NOT_MATCH'));
      return;
    }

    setIsSubmitting(true);

    try {
      await axios.post(
        `${apiBaseUrl}password-reset/confirm?token=${token}`,
        { newPassword: password },
      );
      setIsReset(true);
    } catch {
      setErrorMessage(t('ERROR_GENERIC'));
    } finally {
      setIsSubmitting(false);
    }
  };

  if (tokenIsValid === null) {
    return (
      <Box
        sx={{
          position: 'fixed',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: registerColors.backgroundPanel,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        position: 'fixed',
        inset: 0,
        overflow: 'hidden',
      }}
    >
      <Stack width="100%" height="100%">
        <Stack
          direction="row"
          height="100%"
          sx={{
            overflow: 'hidden',
            '@media (max-width:1023px)': { flexDirection: 'column' },
          }}
        >
          {/* HERO */}
          <Stack width={LAYOUT.heroWidth} height="100%" sx={heroStackSx}>
            <HeroContent />
          </Stack>

          {/* RESET PASSWORD FORM */}
          <Stack
            justifyContent="center"
            alignItems="center"
            width={LAYOUT.formWidth}
            sx={formPanelSx}
          >
            <Box component="img" src={LogoRMF} width="300px" height="auto" sx={{ mb: 2 }} />

            {!tokenIsValid ? (
              <Stack width="100%" spacing={2} mt={4} alignItems="center">
                <Stack spacing={0.5} width="90%" alignItems="center">
                  <Typography variant="h4" fontWeight={700} textAlign="center">
                    {t('RESET_TITLE_LINK_EXPIRED')}
                  </Typography>
                  <Typography
                    variant="body2"
                    textAlign="center"
                    sx={{ color: registerColors.textMuted }}
                  >
                    {t('RESET_TEXT_LINK_EXPIRED')}
                  </Typography>
                </Stack>
                <Button
                  variant="contained"
                  fullWidth
                  component={Link}
                  to="/forgot-password"
                  sx={{ height: '45px', mt: 2 }}
                >
                  {t('RESET_BUTTON_RETRY')}
                </Button>
              </Stack>
            ) : isReset ? (
              <Stack width="100%" spacing={2} mt={4} alignItems="center">
                <Stack spacing={0.5} width="90%" alignItems="center">
                  <Typography variant="h4" fontWeight={700} textAlign="center">
                    {t('RESET_TITLE_SUCCESS')}
                  </Typography>
                  <Typography
                    variant="body2"
                    textAlign="center"
                    sx={{ color: registerColors.textMuted }}
                  >
                    {t('RESET_TEXT_SUCCESS_SHORT')}
                  </Typography>
                </Stack>
                <Stack width="90%" alignItems="center">
                  <Typography
                    variant="body1"
                    textAlign="center"
                    sx={{ color: registerColors.textBody }}
                  >
                    {t('RESET_TEXT_SUCCESS_LONG')}
                  </Typography>
                </Stack>
                <Button
                  variant="contained"
                  fullWidth
                  component={Link}
                  to="/login"
                  sx={{ height: '45px', mt: 2 }}
                >
                  {t('RESET_BUTTON_LOGIN')}
                </Button>
              </Stack>
            ) : (
              <Stack width="100%" spacing={4} mt={4}>
                <Stack spacing={1} alignItems="center">
                  <Typography variant="h4" fontWeight={700} textAlign="center">
                    {t('RESET_TITLE')}
                  </Typography>
                </Stack>

                {errorMessage && (
                  <Alert severity="error">{errorMessage}</Alert>
                )}

                <form onSubmit={handleSubmit} style={{ width: '100%' }}>
                  <Stack spacing={3}>
                    <TextField
                      id="password"
                      label={t('RESET_LABEL_PASSWORD')}
                      variant="outlined"
                      placeholder={t('RESET_LABEL_PASSWORD')}
                      type={showPassword ? 'text' : 'password'}
                      fullWidth
                      autoFocus
                      value={password}
                      error={Boolean(passwordError)}
                      helperText={passwordError || ''}
                      onChange={(e) => setPassword(e.target.value)}
                      onFocus={() => setPasswordFocused(true)}
                      onBlur={() => setPasswordFocused(false)}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                              {showPassword ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />

                    <PasswordStrengthIndicator
                      password={password}
                      visible={passwordFocused}
                      labels={{
                        uppercase: t('AT_LEAST_ONE_UPPERCASE_LETTER'),
                        lowercase: t('AT_LEAST_ONE_LOWERCASE_LETTER'),
                        length: t('AT_LEAST_HEIGHT_CHARACTERS'),
                      }}
                    />

                    <TextField
                      id="confirmPassword"
                      label={t('RESET_LABEL_CONFIRM_PASSWORD')}
                      variant="outlined"
                      placeholder={t('RESET_LABEL_CONFIRM_PASSWORD')}
                      type={showConfirmPassword ? 'text' : 'password'}
                      fullWidth
                      value={confirmPassword}
                      error={Boolean(confirmPasswordError)}
                      helperText={confirmPasswordError || ''}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton onClick={() => setShowConfirmPassword(!showConfirmPassword)} edge="end">
                              {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />

                    <Button
                      type="submit"
                      variant="contained"
                      fullWidth
                      disabled={isSubmitting}
                      sx={{ height: '45px' }}
                    >
                      {isSubmitting ? (
                        <CircularProgress size={24} />
                      ) : (
                        t('RESET_BUTTON_SUBMIT')
                      )}
                    </Button>
                  </Stack>
                </form>
              </Stack>
            )}
          </Stack>
        </Stack>
      </Stack>
    </Box>
  );
};

export default ResetPassword;
